import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type {
  EventOrderNotificationSettings,
  EventOrderFulfillmentStatus,
  EventOrderPaymentStatus,
  EventOrderRecord,
} from '../types';
import { useSiteAuth } from '../hooks/useSiteAuth';
import {
  getEventOrderNotificationRecipient,
  listEventOrders,
  refundEventOrder,
  sendEventOrderReceipt,
  updateEventOrderNotificationRecipient,
  updateEventOrderFulfillmentStatus,
} from '../services/event-orders';
import { filterClientVisibleEventOrders } from '../services/event-order-visibility';

const paymentFilterOptions: Array<{
  value: EventOrderPaymentStatus | 'all';
  label: string;
}> = [
  { value: 'all', label: 'All payments' },
  { value: 'paid', label: 'Paid' },
  { value: 'refunded', label: 'Refunded' },
  { value: 'processing', label: 'Processing' },
  { value: 'canceled', label: 'Canceled' },
];

const fulfillmentFilterOptions: Array<{
  value: EventOrderFulfillmentStatus | 'all';
  label: string;
}> = [
  { value: 'all', label: 'All fulfillment states' },
  { value: 'ready', label: 'Ready' },
  { value: 'fulfilled', label: 'Fulfilled' },
  { value: 'pending', label: 'Pending' },
  { value: 'canceled', label: 'Canceled' },
];

function formatCurrencyFromCents(amountCents: number, currency = 'usd'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format((amountCents || 0) / 100);
}

function formatDate(timestamp: number | null): string {
  if (!timestamp) {
    return 'Not available';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(timestamp * 1000);
}

function formatStatusLabel(value: string): string {
  return value.replace(/-/g, ' ');
}

function getAllowedFulfillmentTransitions(order: EventOrderRecord): EventOrderFulfillmentStatus[] {
  if (order.payment_status === 'paid') {
    return ['ready', 'fulfilled', 'canceled'];
  }

  if (order.payment_status === 'failed' || order.payment_status === 'canceled' || order.payment_status === 'refunded') {
    return ['canceled'];
  }

  return ['pending', 'canceled'];
}

export default function PortalOrders() {
  const { session } = useSiteAuth();
  const [orders, setOrders] = useState<EventOrderRecord[]>([]);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [paymentFilter, setPaymentFilter] = useState<EventOrderPaymentStatus | 'all'>('all');
  const [fulfillmentFilter, setFulfillmentFilter] = useState<EventOrderFulfillmentStatus | 'all'>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isRefunding, setIsRefunding] = useState(false);
  const [isSendingReceipt, setIsSendingReceipt] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [defaultRecipientEmail, setDefaultRecipientEmail] = useState('');
  const [isSavingDefaultRecipient, setIsSavingDefaultRecipient] = useState(false);
  const [savedNotificationSettings, setSavedNotificationSettings] = useState<EventOrderNotificationSettings | null>(null);

  const canManageOrders = session?.role === 'admin';

  useEffect(() => {
    if (!canManageOrders) {
      setOrders([]);
      setSelectedOrderId(null);
      setIsLoading(false);
      return;
    }

    let isActive = true;

    async function loadOrders() {
      try {
        setError(null);
        setFeedback(null);
        if (isActive) {
          setIsLoading(true);
        }

        const fetchedOrders = await listEventOrders({
          paymentStatus: paymentFilter,
          fulfillmentStatus: fulfillmentFilter,
          limit: 150,
        });
        const nextOrders = filterClientVisibleEventOrders(fetchedOrders);

        if (!isActive) {
          return;
        }

        setOrders(nextOrders);
        setSelectedOrderId((current) => {
          if (current && nextOrders.some((order) => order.id === current)) {
            return current;
          }

          return nextOrders[0]?.id ?? null;
        });
      } catch (nextError) {
        if (isActive) {
          setError(nextError instanceof Error ? nextError.message : 'Unable to load event orders right now.');
          setOrders([]);
          setSelectedOrderId(null);
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    }

    void loadOrders();

    return () => {
      isActive = false;
    };
  }, [canManageOrders, fulfillmentFilter, paymentFilter]);

  const selectedOrder = useMemo(
    () => orders.find((order) => order.id === selectedOrderId) ?? null,
    [orders, selectedOrderId],
  );

  const orderSummary = useMemo(() => {
    const paidCount = orders.filter((order) => order.payment_status === 'paid').length;
    const refundedCount = orders.filter((order) => order.payment_status === 'refunded').length;
    const readyCount = orders.filter((order) => order.fulfillment_status === 'ready').length;
    const fulfilledCount = orders.filter((order) => order.fulfillment_status === 'fulfilled').length;
    const grossRevenueCents = orders
      .filter((order) => order.payment_status === 'paid')
      .reduce((total, order) => total + order.charged_amount_cents, 0);

    return {
      paidCount,
      refundedCount,
      readyCount,
      fulfilledCount,
      grossRevenueCents,
    };
  }, [orders]);

  useEffect(() => {
    if (!canManageOrders) {
      setSavedNotificationSettings(null);
      setDefaultRecipientEmail('');
      return;
    }

    let isMounted = true;

    void (async () => {
      try {
        const settings = await getEventOrderNotificationRecipient();
        if (!isMounted) {
          return;
        }

        setSavedNotificationSettings(settings);
        setDefaultRecipientEmail(settings.recipient_email || '');
      } catch {
        if (!isMounted) {
          return;
        }

        setSavedNotificationSettings(null);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [canManageOrders]);

  async function refreshOrders() {
    if (!canManageOrders) {
      return;
    }

    try {
      setIsRefreshing(true);
      setError(null);
      const fetchedOrders = await listEventOrders({
        paymentStatus: paymentFilter,
        fulfillmentStatus: fulfillmentFilter,
        limit: 150,
      });
      const nextOrders = filterClientVisibleEventOrders(fetchedOrders);
      setOrders(nextOrders);
      setSelectedOrderId((current) => {
        if (current && nextOrders.some((order) => order.id === current)) {
          return current;
        }

        return nextOrders[0]?.id ?? null;
      });
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Unable to refresh event orders right now.');
    } finally {
      setIsRefreshing(false);
    }
  }

  async function handleUpdateFulfillmentStatus(nextStatus: EventOrderFulfillmentStatus) {
    if (!selectedOrder || !canManageOrders || selectedOrder.fulfillment_status === nextStatus) {
      return;
    }

    try {
      setIsUpdatingStatus(true);
      setError(null);
      const updatedOrder = await updateEventOrderFulfillmentStatus(selectedOrder.id, nextStatus);
      setOrders((current) =>
        current.map((order) => (order.id === updatedOrder.id ? updatedOrder : order)),
      );
      setSelectedOrderId(updatedOrder.id);
      setFeedback(`Order fulfillment updated to ${formatStatusLabel(updatedOrder.fulfillment_status)}.`);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Unable to update this order right now.');
    } finally {
      setIsUpdatingStatus(false);
    }
  }

  async function handleRefundSelectedOrder() {
    if (!selectedOrder || !canManageOrders || selectedOrder.payment_status !== 'paid') {
      return;
    }

    const confirmed = window.confirm(
      `Issue a full refund for ${selectedOrder.ticket_name} (${formatCurrencyFromCents(
        selectedOrder.charged_amount_cents,
        selectedOrder.currency,
      )})? This will refund the customer and reverse the Bliss application fee.`,
    );
    if (!confirmed) {
      return;
    }

    try {
      setIsRefunding(true);
      setError(null);
      const updatedOrder = await refundEventOrder(selectedOrder.id);
      setOrders((current) =>
        current.map((order) => (order.id === updatedOrder.id ? updatedOrder : order)),
      );
      setSelectedOrderId(updatedOrder.id);
      setFeedback('Refund submitted successfully. The saved event order now reflects the refunded payment.');
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Unable to refund this event order right now.');
    } finally {
      setIsRefunding(false);
    }
  }

  async function handleSendSelectedOrderReceipt() {
    if (!selectedOrder || !canManageOrders || selectedOrder.payment_status !== 'paid') {
      return;
    }

    try {
      setIsSendingReceipt(true);
      setError(null);
      setFeedback(null);
      const result = await sendEventOrderReceipt(selectedOrder.id);
      const buyerStatus = result.buyer.sent ? 'sent' : 'already sent';
      const ownerStatus = result.owner.sent ? 'sent' : 'already sent';
      setFeedback(`Buyer receipt ${buyerStatus} to ${result.buyer.recipient_email}; site-owner notice ${ownerStatus}.`);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Unable to send this payment receipt right now.');
    } finally {
      setIsSendingReceipt(false);
    }
  }

  async function handleSaveDefaultRecipient() {
    try {
      setIsSavingDefaultRecipient(true);
      setError(null);
      setFeedback(null);

      const settings = await updateEventOrderNotificationRecipient({
        recipientEmail: defaultRecipientEmail,
      });

      setSavedNotificationSettings(settings);
      setDefaultRecipientEmail(settings.recipient_email || '');
      if (settings.recipient_email) {
        setFeedback(`New paid ticket and vendor orders will now email ${settings.recipient_email} automatically.`);
      } else {
        setFeedback('Automatic paid-order emails are now turned off.');
      }
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Unable to save the default order recipient right now.');
    } finally {
      setIsSavingDefaultRecipient(false);
    }
  }

  if (!session) {
    return null;
  }

  if (!canManageOrders) {
    return (
      <div className="portal-guard-state">
        <p className="info-kicker">Orders workspace</p>
        <h1>Order management is limited to portal admins.</h1>
        <p>Use the event workspace for public event setup, or contact project leadership if you need order access.</p>
      </div>
    );
  }

  return (
    <motion.div
      className="portal-page"
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
    >
      <section className="portal-section portal-section--feature">
        <div className="portal-section-heading">
          <div>
            <p className="info-kicker">Orders workspace</p>
            <h1>Review paid ticket orders, purchaser details, and fulfillment state.</h1>
          </div>

          <div className="portal-inline-actions">
            <button
              type="button"
              className="button-secondary tap-target"
              onClick={() => void refreshOrders()}
              disabled={isRefreshing || isLoading}
            >
              {isRefreshing ? 'Refreshing...' : 'Refresh orders'}
            </button>
            <Link to="/portal/events" className="button-ghost tap-target">
              Open event workspace
            </Link>
          </div>
        </div>

        <p className="portal-feature-copy">
          Paid event orders are now stored in Bliss and can be reviewed here by admins before check-in,
          follow-up, vendor coordination, or manual fulfillment updates.
        </p>

        <div className="portal-form-grid portal-form-grid--orders">
          <label className="auth-field portal-field-span-full">
            <span>Default email for paid ticket and vendor orders</span>
            <input
              type="email"
              className="auth-input"
              value={defaultRecipientEmail}
              onChange={(event) => setDefaultRecipientEmail(event.target.value)}
              placeholder="siteowner@example.com"
            />
          </label>
        </div>

        <div className="portal-inline-actions">
          <button
            type="button"
            className="button-secondary tap-target"
            onClick={() => void handleSaveDefaultRecipient()}
            disabled={isSavingDefaultRecipient}
          >
            {isSavingDefaultRecipient ? 'Saving default email...' : 'Save default email'}
          </button>
        </div>

        {savedNotificationSettings?.recipient_email ? (
          <p className="portal-feature-copy">
            New paid ticket and vendor orders are currently emailed automatically to {savedNotificationSettings.recipient_email}.
          </p>
        ) : (
          <p className="portal-feature-copy">
            Save a default email above if each new paid ticket sale or vendor order should send automatically as it happens.
          </p>
        )}

        {feedback ? <div className="auth-success">{feedback}</div> : null}
        {error ? <div className="auth-runtime-note"><p>{error}</p></div> : null}

        <div className="portal-runtime-list">
          <div className="portal-runtime-row">
            <span>Total orders in view</span>
            <strong>{isLoading ? '...' : orders.length}</strong>
          </div>
          <div className="portal-runtime-row">
            <span>Paid orders</span>
            <strong>{isLoading ? '...' : orderSummary.paidCount}</strong>
          </div>
          <div className="portal-runtime-row">
            <span>Refunded orders</span>
            <strong>{isLoading ? '...' : orderSummary.refundedCount}</strong>
          </div>
          <div className="portal-runtime-row">
            <span>Ready for fulfillment</span>
            <strong>{isLoading ? '...' : orderSummary.readyCount}</strong>
          </div>
          <div className="portal-runtime-row">
            <span>Fulfilled orders</span>
            <strong>{isLoading ? '...' : orderSummary.fulfilledCount}</strong>
          </div>
          <div className="portal-runtime-row">
            <span>Gross paid volume</span>
            <strong>{isLoading ? '...' : formatCurrencyFromCents(orderSummary.grossRevenueCents)}</strong>
          </div>
        </div>
      </section>

      <div className="portal-orders-grid">
        <section className="portal-section portal-orders-list-panel">
          <div className="portal-section-heading">
            <div>
              <p className="info-kicker">Filters</p>
              <h2>Current event orders</h2>
            </div>
          </div>

          <div className="portal-form-grid portal-form-grid--orders">
            <label className="auth-field">
              <span>Payment status</span>
              <select
                className="auth-input"
                value={paymentFilter}
                onChange={(event) => setPaymentFilter(event.target.value as EventOrderPaymentStatus | 'all')}
              >
                {paymentFilterOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="auth-field">
              <span>Fulfillment status</span>
              <select
                className="auth-input"
                value={fulfillmentFilter}
                onChange={(event) =>
                  setFulfillmentFilter(event.target.value as EventOrderFulfillmentStatus | 'all')
                }
              >
                {fulfillmentFilterOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="portal-orders-list">
            {isLoading ? (
              <div className="auth-runtime-note">
                <p>Loading saved event orders...</p>
              </div>
            ) : orders.length ? (
              orders.map((order) => (
                <button
                  key={order.id}
                  type="button"
                  onClick={() => setSelectedOrderId(order.id)}
                  className={`portal-order-card ${selectedOrderId === order.id ? 'portal-order-card--active' : ''}`}
                >
                  <div className="portal-order-card-row">
                    <strong>{order.purchaser_name || order.purchaser_email}</strong>
                    <span className={`portal-order-status-badge portal-order-status-badge--${order.payment_status}`}>
                      {formatStatusLabel(order.payment_status)}
                    </span>
                  </div>
                  <p>{order.event_title}</p>
                  <div className="portal-order-card-meta">
                    <span>{order.ticket_name}{order.ticket_quantity > 1 ? ` x${order.ticket_quantity}` : ''}</span>
                    <span>{formatCurrencyFromCents(order.charged_amount_cents, order.currency)}</span>
                  </div>
                  <div className="portal-order-card-meta">
                    <span>{formatDate(order.created_at)}</span>
                    <span>{formatStatusLabel(order.fulfillment_status)}</span>
                  </div>
                </button>
              ))
            ) : (
              <div className="auth-runtime-note">
                <p>No event orders match the current filters yet.</p>
              </div>
            )}
          </div>
        </section>

        <section className="portal-section portal-order-detail-panel">
          {selectedOrder ? (
            <>
              <div className="portal-section-heading">
                <div>
                  <p className="info-kicker">Order detail</p>
                  <h2>{selectedOrder.purchaser_name || selectedOrder.purchaser_email}</h2>
                </div>

                <div className="portal-inline-actions">
                  <span className={`portal-order-status-badge portal-order-status-badge--${selectedOrder.payment_status}`}>
                    Payment {formatStatusLabel(selectedOrder.payment_status)}
                  </span>
                  <span className={`portal-order-status-badge portal-order-status-badge--fulfillment-${selectedOrder.fulfillment_status}`}>
                    Fulfillment {formatStatusLabel(selectedOrder.fulfillment_status)}
                  </span>
                </div>
              </div>

              <div className="portal-order-detail-grid">
                <article className="portal-account-card">
                  <p className="info-kicker">Purchaser</p>
                  <h3>{selectedOrder.purchaser_name || 'Name not provided'}</h3>
                  <p className="portal-detail-copy">{selectedOrder.purchaser_email}</p>
                </article>

                <article className="portal-account-card">
                  <p className="info-kicker">Event</p>
                  <h3>{selectedOrder.event_title}</h3>
                  <p className="portal-detail-copy">{selectedOrder.ticket_name}</p>
                </article>

                {selectedOrder.vendor_business_name || selectedOrder.vendor_contact_name ? (
                  <article className="portal-account-card">
                    <p className="info-kicker">Vendor registration</p>
                    <h3>{selectedOrder.vendor_business_name || 'Business pending'}</h3>
                    <p className="portal-detail-copy">
                      {selectedOrder.vendor_contact_name || selectedOrder.vendor_email || 'Contact pending'}
                    </p>
                  </article>
                ) : null}
              </div>

              <div className="portal-runtime-list">
                <div className="portal-runtime-row">
                  <span>Order ID</span>
                  <strong>{selectedOrder.id}</strong>
                </div>
                <div className="portal-runtime-row">
                  <span>Ticket category</span>
                  <strong>{formatStatusLabel(selectedOrder.ticket_category)}</strong>
                </div>
                <div className="portal-runtime-row">
                  <span>Quantity</span>
                  <strong>{selectedOrder.ticket_quantity}</strong>
                </div>
                <div className="portal-runtime-row">
                  <span>Created</span>
                  <strong>{formatDate(selectedOrder.created_at)}</strong>
                </div>
                <div className="portal-runtime-row">
                  <span>Paid at</span>
                  <strong>{formatDate(selectedOrder.paid_at)}</strong>
                </div>
                <div className="portal-runtime-row">
                  <span>Refunded at</span>
                  <strong>{formatDate(selectedOrder.refunded_at)}</strong>
                </div>
                <div className="portal-runtime-row">
                  <span>Base ticket amount</span>
                  <strong>{formatCurrencyFromCents(selectedOrder.base_amount_cents, selectedOrder.currency)}</strong>
                </div>
                <div className="portal-runtime-row">
                  <span>Customer processing fee</span>
                  <strong>{formatCurrencyFromCents(selectedOrder.processing_fee_cents, selectedOrder.currency)}</strong>
                </div>
                <div className="portal-runtime-row">
                  <span>Total charged</span>
                  <strong>{formatCurrencyFromCents(selectedOrder.charged_amount_cents, selectedOrder.currency)}</strong>
                </div>
                <div className="portal-runtime-row">
                  <span>Estimated Stripe fee</span>
                  <strong>{formatCurrencyFromCents(selectedOrder.estimated_stripe_fee_cents, selectedOrder.currency)}</strong>
                </div>
                <div className="portal-runtime-row">
                  <span>Bliss application fee</span>
                  <strong>{formatCurrencyFromCents(selectedOrder.application_fee_cents, selectedOrder.currency)}</strong>
                </div>
                <div className="portal-runtime-row">
                  <span>Stripe payment intent</span>
                  <strong>{selectedOrder.stripe_payment_intent_id || 'Not available'}</strong>
                </div>
                <div className="portal-runtime-row">
                  <span>Stripe refund</span>
                  <strong>{selectedOrder.stripe_refund_id || 'Not available'}</strong>
                </div>
              </div>

              {selectedOrder.vendor_business_name || selectedOrder.vendor_contact_name ? (
                <div className="portal-runtime-list">
                  <div className="portal-runtime-row">
                    <span>Vendor type</span>
                    <strong>{selectedOrder.vendor_profile_type ? formatStatusLabel(selectedOrder.vendor_profile_type) : 'Vendor'}</strong>
                  </div>
                  <div className="portal-runtime-row">
                    <span>Business</span>
                    <strong>{selectedOrder.vendor_business_name || 'Not provided'}</strong>
                  </div>
                  <div className="portal-runtime-row">
                    <span>Contact</span>
                    <strong>{selectedOrder.vendor_contact_name || 'Not provided'}</strong>
                  </div>
                  <div className="portal-runtime-row">
                    <span>Vendor email</span>
                    <strong>{selectedOrder.vendor_email || 'Not provided'}</strong>
                  </div>
                  <div className="portal-runtime-row">
                    <span>Vendor phone</span>
                    <strong>{selectedOrder.vendor_phone || 'Not provided'}</strong>
                  </div>
                  <div className="portal-runtime-row">
                    <span>Website</span>
                    <strong>{selectedOrder.vendor_website_url || 'Not provided'}</strong>
                  </div>
                  <div className="portal-runtime-row">
                    <span>Instagram</span>
                    <strong>{selectedOrder.vendor_instagram_handle || 'Not provided'}</strong>
                  </div>
                </div>
              ) : null}

              {selectedOrder.vendor_description ? (
                <div className="auth-runtime-note">
                  <p className="info-kicker">Vendor description</p>
                  <p>{selectedOrder.vendor_description}</p>
                </div>
              ) : null}

              {selectedOrder.vendor_booth_details ? (
                <div className="auth-runtime-note">
                  <p className="info-kicker">Booth notes</p>
                  <p>{selectedOrder.vendor_booth_details}</p>
                </div>
              ) : null}

              <div className="portal-order-actions">
                <div>
                  <p className="info-kicker">Order actions</p>
                  <h3>Move fulfillment forward or issue a refund when needed.</h3>
                </div>

                <div className="portal-inline-actions">
                  {selectedOrder.payment_status === 'paid' ? (
                    <>
                      <button
                        type="button"
                        className="button-secondary tap-target"
                        onClick={() => void handleSendSelectedOrderReceipt()}
                        disabled={isSendingReceipt || isRefunding || isUpdatingStatus}
                      >
                        {isSendingReceipt ? 'Sending emails...' : 'Send buyer and owner emails'}
                      </button>
                      <button
                        type="button"
                        className="button-secondary tap-target"
                        onClick={() => void handleRefundSelectedOrder()}
                        disabled={isSendingReceipt || isRefunding || isUpdatingStatus}
                      >
                        {isRefunding ? 'Refunding...' : 'Issue full refund'}
                      </button>
                    </>
                  ) : null}
                  {getAllowedFulfillmentTransitions(selectedOrder).map((status) => (
                    <button
                      key={status}
                      type="button"
                      className={`button-ghost tap-target ${selectedOrder.fulfillment_status === status ? 'portal-order-action--active' : ''}`}
                      onClick={() => void handleUpdateFulfillmentStatus(status)}
                      disabled={isUpdatingStatus || selectedOrder.fulfillment_status === status}
                    >
                      {selectedOrder.fulfillment_status === status ? `Current: ${formatStatusLabel(status)}` : `Mark ${formatStatusLabel(status)}`}
                    </button>
                  ))}
                </div>
              </div>

              {selectedOrder.last_error ? (
                <div className="auth-runtime-note">
                  <p className="info-kicker">Last payment note</p>
                  <p>{selectedOrder.last_error}</p>
                </div>
              ) : null}
            </>
          ) : (
            <div className="auth-runtime-note">
              <p>Select an order to review the purchaser, totals, and fulfillment status.</p>
            </div>
          )}
        </section>
      </div>
    </motion.div>
  );
}
